/**
 * 자동 실행 결과 리포트 모달 — 만공차/조명/비상벨 3개 탭이 공유한다.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { CheckCircle as PassIcon, Cancel as FailIcon } from '@mui/icons-material';
import type { AutoCheckResult } from './autoTestUtils';

interface AutoTestReportDialogProps {
  open: boolean;
  title: string;
  results: AutoCheckResult[];
  onClose: () => void;
}

const AutoTestReportDialog: React.FC<AutoTestReportDialogProps> = ({
  open,
  title,
  results,
  onClose,
}) => {
  const passCount = results.filter(r => r.passed).length;
  const allPassed = results.length > 0 && passCount === results.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            테스트할 대상이 없습니다.
          </Typography>
        ) : (
          <>
            <Alert severity={allPassed ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
              {passCount}/{results.length} 통과
              {!allPassed && ` · ${results.length - passCount}건 확인 필요`}
            </Alert>
            <Divider sx={{ mb: 1 }} />
            <List dense disablePadding>
              {results.map((r, i) => (
                <ListItem key={`${r.name}-${i}`} disableGutters alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    {r.passed ? (
                      <PassIcon color="success" fontSize="small" />
                    ) : (
                      <FailIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {r.category && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={r.category}
                            sx={{ height: 18 }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {r.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={r.passed ? '통과' : '실패'}
                          color={r.passed ? 'success' : 'error'}
                          sx={{ height: 18 }}
                        />
                      </Box>
                    }
                    secondary={r.detail}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoTestReportDialog;
